import React from 'react';
import { Link } from 'react-router';
import ProcedenciaLead from './ProcedenciaLead';

/**
 * Los leads por procedencia (dirección, 27/09/2026: «para luego tener un buen
 * recuento de qué hacemos con cada cosa»). Lo cuenta el servidor
 * (procedencia_lead.recuento, con las mismas reglas que la lista de Leads) y
 * aquí se pinta: primero las campañas, cada una con su número, y luego las
 * demás vías; cada procedencia lleva a la lista de Leads filtrada por ella.
 *
 *  · Recibidos: todos los que han llegado, también los descartados y los ya
 *    citados (dirección, 28/9/2026: descartar en Leads no cambia lo recibido).
 *  · Contactados y ya citados: de los recibidos. Ya citados, desde el
 *    16/10/2026, con una cita POSTERIOR a cuando preguntaron (o a mano).
 *  · Ya eran pacientes: de los recibidos, los que solo tenían citas de antes
 *    (aparte, sin sumar en ya citados).
 *  · Descartados: de los recibidos, los quitados de la lista de Leads.
 *
 * Lo usan Indicadores (todas) y Campañas (solo las campañas, con cuántos
 * entraron por el formulario de Meta: lo que suman sus filas).
 *
 * Una fila por OPCIÓN (30/09/2026: las campañas de los tratamientos con
 * campaña, Web, Instagram, TikTok y Otras). «Otras» dice debajo qué lleva
 * dentro (`detalles`: «Llamada 2 · Recomendación 1»), para que lo que antes
 * tenía fila propia no se pierda de vista.
 */
// «Ya citados» (16/10/2026): con una cita posterior a cuando preguntaron, o
// marcados a mano. «Ya eran pacientes», aparte: solo tenían citas de antes, y
// no suman en «Ya citados».
const COLUMNAS = [
  { k: 'recibidos', nombre: 'Recibidos' },
  { k: 'por_formulario', nombre: 'Por el formulario de Meta', soloFormulario: true },
  { k: 'contactados', nombre: 'Contactados' },
  { k: 'citados', nombre: 'Ya citados' },
  { k: 'ya_pacientes', nombre: 'Ya eran pacientes' },
  { k: 'descartados', nombre: 'Descartados' },
];

export default function RecuentoProcedencia({
  filas, totales, soloCampanas = false, conFormulario = false, testid = 'recuento-procedencia',
}) {
  const columnas = COLUMNAS.filter((c) => conFormulario || !c.soloFormulario);
  const campanas = (filas || []).filter((f) => f.grupo === 'campana');
  const vias = soloCampanas ? [] : (filas || []).filter((f) => f.grupo !== 'campana');

  const fila = (f) => {
    const vacia = !f.recibidos && !f.descartados;
    return (
      <tr key={f.clave} className={`border-b last:border-0 ${vacia ? 'text-muted-foreground' : ''}`}
        data-testid={`${testid}-${f.clave}`}>
        <td className="py-1.5 pr-3 pl-3">
          <Link to={`/leads?procedencia=${encodeURIComponent(f.clave)}`} className="hover:opacity-80"
            title="Ver estos contactos en Leads">
            <ProcedenciaLead procedencia={f} />
          </Link>
          {f.detalles?.length ? (
            <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`${testid}-${f.clave}-detalles`}>
              {f.detalles.map((d) => `${d.detalle || 'sin decir cuál'} ${d.recibidos}`).join(' · ')}
            </div>
          ) : null}
        </td>
        {columnas.map((c) => (
          <td key={c.k} className={`py-1.5 pr-3 text-right tabular-nums ${c.k === 'recibidos' ? 'font-medium' : ''}`}
            data-testid={`${testid}-${f.clave}-${c.k}`}>
            {f[c.k] ?? 0}
          </td>
        ))}
      </tr>
    );
  };

  const subtotal = (nombre, t, clave) => (
    <tr className="bg-muted/40 text-xs font-semibold" data-testid={`${testid}-${clave}`}>
      <td className="py-1.5 pr-3">{nombre}</td>
      {columnas.map((c) => (
        <td key={c.k} className="py-1.5 pr-3 text-right tabular-nums" data-testid={`${testid}-${clave}-${c.k}`}>
          {t?.[c.k] ?? 0}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full max-w-3xl text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className="py-2 pr-3">Procedencia</th>
            {columnas.map((c) => <th key={c.k} className="py-2 pr-3 text-right">{c.nombre}</th>)}
          </tr>
        </thead>
        <tbody>
          {subtotal(soloCampanas ? 'Todas las campañas' : 'Campañas', totales?.campanas, 'campanas')}
          {campanas.map(fila)}
          {vias.length ? subtotal('Otras vías', totales?.vias, 'vias') : null}
          {vias.map(fila)}
          {!soloCampanas ? subtotal('Total', totales?.todos, 'total') : null}
        </tbody>
      </table>
    </div>
  );
}
